/**
 * P-ARC-02: RolePackEditor
 *
 * Slide-in panel for Tenant Admin to customise a role pack's capabilities
 * within the platform governance envelope.
 *
 * Design:
 * - Each toggle is saved immediately (no batch "Save" button needed).
 * - Version is tracked client-side; conflicts surface a 409 toast.
 * - HIGH risk toggles require an explicit confirmation modal.
 * - CRITICAL capabilities are read-only with a platform-lock indicator.
 * - Protected capabilities are shown locked (cannot be revoked).
 */

import { useState, useEffect, useCallback } from "react";
import { X, Lock, ShieldAlert, ChevronDown, ChevronRight, RotateCcw, CheckCircle2, AlertTriangle } from "lucide-react";
import { catalogApi } from "../services/api";
import { toast } from "sonner";

// ── Risk badge config ─────────────────────────────────────────────────────────
const RISK_CONFIG = {
  LOW:      { label: "Low",      color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  MEDIUM:   { label: "Medium",   color: "bg-amber-100  text-amber-700  border-amber-200"  },
  HIGH:     { label: "High",     color: "bg-orange-100 text-orange-700 border-orange-200" },
  CRITICAL: { label: "Critical", color: "bg-red-100    text-red-700    border-red-200"    },
};

// ── Status dot config ─────────────────────────────────────────────────────────
const STATUS_ICON = {
  PLATFORM_PROTECTED: <Lock className="w-3.5 h-3.5 text-[#5E4DB2]"      title="Protected — required by this role" />,
  PLATFORM:           null,
  GRANTED:            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" title="Tenant added" />,
  REVOKED:            null,
  AVAILABLE:          null,
  NOT_GRANTABLE:      <ShieldAlert className="w-3.5 h-3.5 text-slate-400" title="Platform governed — not tenant-grantable" />,
};


// ── CapabilityRow ─────────────────────────────────────────────────────────────
function CapabilityRow({ cap, overrideVersion, onToggled, saving }) {
  const isCritical    = cap.risk_level === "CRITICAL";
  const isLocked      = isCritical || cap.status === "PLATFORM_PROTECTED";
  const isNotGrantable = cap.status === "NOT_GRANTABLE";
  const isChecked     = ["PLATFORM_PROTECTED", "PLATFORM", "GRANTED"].includes(cap.status);
  const isRevoked     = cap.status === "REVOKED";
  const isDisabled    = isLocked || isNotGrantable || saving;

  const risk = RISK_CONFIG[cap.risk_level] || RISK_CONFIG.LOW;

  const handleToggle = () => {
    if (isDisabled) return;
    const action = isChecked ? "revoke" : "grant";
    onToggled(cap, action);
  };

  const rowOpacity = isNotGrantable ? "opacity-40" : isRevoked ? "opacity-50" : "";

  return (
    <div
      className={`flex items-start gap-3 py-2.5 px-3 rounded-lg group hover:bg-[#F7F7F8] transition-colors ${rowOpacity}`}
      data-testid={`cap-row-${cap.id}`}
    >
      {/* Checkbox */}
      <div className="mt-0.5 flex-shrink-0">
        {isLocked ? (
          <div className="w-4 h-4 rounded border-2 border-[#C2B0FF] bg-[#F0EDFF] flex items-center justify-center">
            <Lock className="w-2.5 h-2.5 text-[#5E4DB2]" />
          </div>
        ) : (
          <input
            type="checkbox"
            checked={isChecked}
            disabled={isDisabled}
            onChange={handleToggle}
            className="w-4 h-4 rounded accent-[#5E4DB2] cursor-pointer disabled:cursor-not-allowed"
            data-testid={`cap-toggle-${cap.id}`}
          />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[13px] font-medium ${isRevoked ? "line-through text-slate-400" : "text-[#1A1A2E]"}`}>
            {cap.display_name}
          </span>
          {STATUS_ICON[cap.status]}
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${risk.color}`}>
            {risk.label}
          </span>
          {cap.status === "GRANTED" && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
              Added
            </span>
          )}
          {isRevoked && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-orange-50 text-orange-600 border border-orange-200">
              Removed
            </span>
          )}
        </div>
        {cap.description && (
          <p className="text-[11px] text-[#706E6B] mt-0.5 leading-snug">{cap.description}</p>
        )}
        {isNotGrantable && (
          <p className="text-[11px] text-slate-400 mt-0.5 italic">Platform governed — not tenant-configurable</p>
        )}
      </div>
    </div>
  );
}


// ── CapabilityGroup ───────────────────────────────────────────────────────────
function CapabilityGroup({ group, overrideVersion, onToggled, saving }) {
  const [open, setOpen] = useState(true);
  const inEffective = group.capabilities.filter(c => c.in_effective).length;
  const total = group.capabilities.length;

  return (
    <div className="mb-2">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-[#F3F0FF] transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-[#5E4DB2]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#5E4DB2]" />}
          <span className="text-[12px] font-semibold text-[#5E4DB2] uppercase tracking-wide">
            {group.group}
          </span>
        </div>
        <span className="text-[11px] text-[#706E6B]">{inEffective} / {total} active</span>
      </button>
      {open && (
        <div className="ml-1">
          {group.capabilities.map(cap => (
            <CapabilityRow
              key={cap.id}
              cap={cap}
              overrideVersion={overrideVersion}
              onToggled={onToggled}
              saving={saving}
            />
          ))}
        </div>
      )}
    </div>
  );
}


// ── HIGH-risk confirmation modal ──────────────────────────────────────────────
function HighRiskConfirmModal({ confirm, roleName, onConfirm, onCancel }) {
  if (!confirm) return null;
  const { cap, action } = confirm;
  const verb = action === "grant" ? "grant" : "remove";
  const direction = action === "grant" ? "granting" : "removing";

  return (
    <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h3 className="font-semibold text-[#1A1A2E] text-sm">High-Risk Permission Change</h3>
            <p className="text-xs text-[#706E6B]">This change takes effect immediately for all users in this role.</p>
          </div>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-5">
          <p className="text-sm text-orange-800">
            You are {direction} <strong>{roleName || "this role"}</strong>'s permission to{" "}
            <strong>{cap.display_name.toLowerCase()}</strong>.
            This will affect all users with this role.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 rounded-xl border border-[#E5E5E5] text-sm text-[#706E6B] hover:bg-[#F7F7F8] transition-colors"
            data-testid="high-risk-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 rounded-xl bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 transition-colors"
            data-testid="high-risk-confirm-btn"
          >
            {action === "grant" ? "Grant Access" : "Remove Access"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── RolePackEditor (main export) ──────────────────────────────────────────────
export default function RolePackEditor({ roleId, onClose }) {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [overrideVersion, setOverrideVersion] = useState(0);
  const [confirmModal, setConfirmModal]       = useState(null); // {cap, action}
  const [resetConfirm, setResetConfirm]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await catalogApi.getEffectiveRolePack(roleId);
      setData(res.data);
      setOverrideVersion(res.data.override_version ?? 0);
    } catch (err) {
      toast.error("Could not load role pack: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => { load(); }, [load]);

  // ── Toggle handler ────────────────────────────────────────────────────────
  const handleToggled = useCallback((cap, action) => {
    if (cap.risk_level === "HIGH") {
      setConfirmModal({ cap, action });
      return;
    }
    applyToggle(cap, action);
  }, [overrideVersion]); // eslint-disable-line

  const applyToggle = useCallback(async (cap, action) => {
    setConfirmModal(null);
    setSaving(true);
    try {
      await catalogApi.toggleCapability(roleId, {
        capability_id: cap.id,
        action,
        expected_version: overrideVersion,
      });
      toast.success(
        action === "grant"
          ? `'${cap.display_name}' added.`
          : `'${cap.display_name}' removed.`
      );
      // Reload to get new state + version
      const res = await catalogApi.getEffectiveRolePack(roleId);
      setData(res.data);
      setOverrideVersion(res.data.override_version ?? 0);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.message;
      if (status === 409) {
        toast.error("Permissions changed since you opened this page. Refreshing…");
        load();
      } else {
        toast.error(detail);
      }
    } finally {
      setSaving(false);
    }
  }, [roleId, overrideVersion, load]);

  // ── Reset handler ─────────────────────────────────────────────────────────
  const handleReset = useCallback(async () => {
    setResetConfirm(false);
    setSaving(true);
    try {
      await catalogApi.resetRolePack(roleId, overrideVersion);
      toast.success("Role pack reset to platform default.");
      const res = await catalogApi.getEffectiveRolePack(roleId);
      setData(res.data);
      setOverrideVersion(res.data.override_version ?? 0);
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        toast.error("Permissions changed since you opened this page. Refreshing…");
        load();
      } else {
        toast.error(err.response?.data?.detail || err.message);
      }
    } finally {
      setSaving(false);
    }
  }, [roleId, overrideVersion, load]);

  const isCustomised = data?.customisation_state === "CUSTOMISED";
  const summary      = data?.summary || {};

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-[60]"
        onClick={onClose}
        data-testid="role-pack-editor-backdrop"
      />

      {/* Slide-in panel */}
      <div
        className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-[70] flex flex-col"
        data-testid="role-pack-editor-panel"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-[#E5E5E5] flex-shrink-0">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-base font-semibold text-[#1A1A2E]" data-testid="editor-role-name">
                {data?.display_name || roleId}
              </h2>
              {data && (
                <span
                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wide ${
                    isCustomised
                      ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-[#F3F0FF] text-[#5E4DB2] border-[#C2B0FF]"
                  }`}
                  data-testid="customisation-badge"
                >
                  {isCustomised ? "Customised" : "Platform Default"}
                </span>
              )}
            </div>
            {data?.description && (
              <p className="text-[12px] text-[#706E6B] max-w-md">{data.description}</p>
            )}
            {data && (
              <p className="text-[11px] text-[#706E6B] mt-1">
                {summary.effective_count} effective capabilities
                {isCustomised && (
                  <> · <span className="text-emerald-600">+{summary.granted_additions} added</span>
                  {summary.revocations > 0 && <> · <span className="text-orange-600">−{summary.revocations} removed</span></>}
                  </>
                )}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isCustomised && (
              <button
                onClick={() => setResetConfirm(true)}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-[#E5E5E5] text-[12px] text-[#706E6B] hover:bg-[#F7F7F8] transition-colors disabled:opacity-50"
                data-testid="reset-to-default-btn"
              >
                <RotateCcw className="w-3 h-3" />
                Reset to Default
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-[#F7F7F8] transition-colors"
              data-testid="close-editor-btn"
            >
              <X className="w-4 h-4 text-[#706E6B]" />
            </button>
          </div>
        </div>

        {/* Governance legend */}
        <div className="px-6 py-2 bg-[#FAFAFA] border-b border-[#E5E5E5] flex items-center gap-4 flex-shrink-0">
          <span className="text-[11px] text-[#706E6B] font-medium">Legend:</span>
          <div className="flex items-center gap-1">
            <Lock className="w-3 h-3 text-[#5E4DB2]" />
            <span className="text-[11px] text-[#706E6B]">Protected core</span>
          </div>
          <div className="flex items-center gap-1">
            <ShieldAlert className="w-3 h-3 text-slate-400" />
            <span className="text-[11px] text-[#706E6B]">Platform governed</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-orange-100 text-orange-700 border-orange-200">High</span>
            <span className="text-[11px] text-[#706E6B]">Requires confirmation</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-red-100 text-red-700 border-red-200">Critical</span>
            <span className="text-[11px] text-[#706E6B]">Read-only</span>
          </div>
        </div>

        {/* Capability list */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="text-[13px] text-[#706E6B] animate-pulse">Loading capabilities…</div>
            </div>
          ) : data ? (
            data.capability_groups.map(group => (
              <CapabilityGroup
                key={group.group}
                group={group}
                overrideVersion={overrideVersion}
                onToggled={handleToggled}
                saving={saving}
              />
            ))
          ) : (
            <div className="text-center text-[13px] text-[#706E6B] py-12">
              Failed to load capability data.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#E5E5E5] flex items-center justify-between flex-shrink-0 bg-white">
          <p className="text-[11px] text-[#706E6B]">
            {saving ? "Saving…" : "Changes take effect immediately for all users in this role."}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-[#1A1A2E] text-white text-[13px] font-medium hover:bg-[#2D2D4E] transition-colors"
            data-testid="done-btn"
          >
            Done
          </button>
        </div>
      </div>

      {/* HIGH-risk confirmation modal */}
      <HighRiskConfirmModal
        confirm={confirmModal}
        roleName={data?.display_name}
        onConfirm={() => confirmModal && applyToggle(confirmModal.cap, confirmModal.action)}
        onCancel={() => setConfirmModal(null)}
      />

      {/* Reset confirmation */}
      {resetConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <h3 className="font-semibold text-[#1A1A2E] mb-2">Reset to Platform Default?</h3>
            <p className="text-[13px] text-[#706E6B] mb-5">
              All custom grants and revocations for{" "}
              <strong>{data?.display_name}</strong> will be removed. The role
              will use the platform-managed defaults from this point forward.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setResetConfirm(false)}
                className="flex-1 px-4 py-2 rounded-xl border border-[#E5E5E5] text-sm text-[#706E6B] hover:bg-[#F7F7F8]"
                data-testid="reset-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                className="flex-1 px-4 py-2 rounded-xl bg-[#1A1A2E] text-white text-sm font-medium hover:bg-[#2D2D4E]"
                data-testid="reset-confirm-btn"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
